import React, { FC, useCallback, useEffect, useMemo } from 'react';
import {
  getUpdateCacheOnUpdateIsReadTender,
  useOpenTenderPreview,
  useUpdateTender
} from 'src/models/procurements/hooks';
import { useTenderPreview } from 'src/models/procurements/Preview/hooks';
import { AssignToTender, LinkToBuyer, RejectTendersButton, WorkspacePicker } from 'src/shared';
import styles from './index.module.scss';
import { BidItemType } from '@tendium/prom-types/tender';
import { TenderStatusBadge } from 'src/common';
import { ProcurementsDisplay } from 'src/models/procurements/types';
import useOverviewMode from 'src/reactiveVars/overviewModeVar';
import { isFieldString } from 'src/models/procurements/Tender/types';
import { isBoolean } from 'src/helpers';

export const TenderPreviewHeader: FC = () => {
  const { data } = useTenderPreview();

  const procurementTabInfo = useOverviewMode();

  const isMatchedTab = useMemo(() => {
    return procurementTabInfo?.tab === ProcurementsDisplay.MATCHED;
  }, [procurementTabInfo]);

  const tenderId = data?.id;
  const boxes = data?.boxes;
  const nextTender = data?.nextId;
  const assignedTo = useMemo(() => data?.assignedTo ?? null, [data]);
  const name = useMemo(
    () =>
      boxes?.name && boxes.name.length && isFieldString(boxes.name[0].firstField)
        ? boxes.name[0].firstField.string
        : '',
    [boxes]
  );
  const buyerBox = useMemo(
    () => (boxes?.buyerBoxes && boxes.buyerBoxes.length ? boxes.buyerBoxes[0] : undefined),
    [boxes]
  );
  const linkToBuyerBox = useMemo(
    () => (boxes?.linkToProcurerBoxes && boxes.linkToProcurerBoxes.length ? boxes.linkToProcurerBoxes[0] : undefined),
    [boxes]
  );

  const openNextTenderPreview = useOpenTenderPreview();

  const onOpenNextTenderPreview = useCallback(() => {
    openNextTenderPreview(nextTender);
  }, [nextTender, openNextTenderPreview]);

  const [updateTender] = useUpdateTender();
  useEffect(() => {
    if (isBoolean(data?.isRead) && !data?.isRead && tenderId) {
      updateTender({
        variables: { tenderId, isRead: true },
        update: getUpdateCacheOnUpdateIsReadTender(tenderId, true)
      }).catch(err => console.error(err));
    }
  }, [data?.isRead, tenderId, updateTender]);

  return (
    data && (
      <>
        <div className={styles.heading}>
          <div className={styles.title}>
            <div className={styles.tenderStatusBadge}>{<TenderStatusBadge statuses={data.procurementStatus} />}</div>
            {name}
          </div>
          {tenderId && !data.isRejected && (
            <div className={styles.assignToTender}>
              <AssignToTender procId={tenderId} assignedTo={assignedTo} eventSource={'ProcurementPreview'} />
            </div>
          )}
        </div>
        <LinkToBuyer linkBox={linkToBuyerBox} buyerBox={buyerBox} className={styles.buyerInfo} />
        {tenderId && (
          <div className={styles.headingActions}>
            <div className={styles.headingActionsLeft}>
              <div className={styles.headingActionLeft}>
                <WorkspacePicker
                  originIds={data.bid ? undefined : [tenderId]}
                  originType={data.bid ? undefined : BidItemType.Procurement}
                  bidIds={data.bid ? [data.bid] : undefined}
                  eventSource={'TenderPreview'}
                  mode={'full'}
                  onChange={onOpenNextTenderPreview}
                />
              </div>
              {isMatchedTab && (
                <div className={styles.headingActionLeft}>
                  <RejectTendersButton
                    procurements={[{ id: data.id, isRejected: data.isRejected }]}
                    eventSource={'ProcurementPreview'}
                    mode={'full'}
                    onFinish={() => !data.isRejected && onOpenNextTenderPreview()}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </>
    )
  );
};

export default TenderPreviewHeader;
